// src/components/SceneLayers/SelectionHighlights/SelectionHighlights.tsx
import React from 'react';
import { useUiStateStore } from '@stores/uiStateStore';
import { useScene } from '@hooks/useScene';
import { useIsoProjection } from '@hooks/useIsoProjection';
import { Svg } from '@/components/Svg/Svg';
import { SceneLayer } from '@/components/SceneLayer/SceneLayer';
import { RenderLayer } from '@/core/renderer/types';
import { ItemReference } from '@/types';
import { useGroupStore } from '@stores/groupStore';

export const SelectionHighlights: React.FC = () => {
  const itemControls = useUiStateStore((state) => state.itemControls);
  const scene = useScene();
  const groups = useGroupStore((state) => state.groups);
  const groupActions = useGroupStore((state) => state.actions);

  // Only show highlights in MULTI_SELECT mode
  if (itemControls?.type !== 'MULTI_SELECT') {
    return null;
  }

  // Once a multi-selection has become a group, GroupHighlights renders the
  // combined footprint. Per-member tile outlines would duplicate that state
  // and make unrelated coverage tiles appear selected.
  const selectedGroups = itemControls.items.map((item) =>
    groupActions.getGroupForElement({ type: item.type as any, id: item.id })
  );
  const firstGroup = selectedGroups[0];
  const isSingleCompleteGroup = selectedGroups.length > 0 &&
    firstGroup !== undefined &&
    groups.some((group) => group.id === firstGroup.id) &&
    selectedGroups.every((group) => group?.id === firstGroup.id) &&
    groupActions.getGroupElements(firstGroup.id).length === itemControls.items.length;

  if (isSingleCompleteGroup) {
    return null;
  }

  return (
    <SceneLayer order={RenderLayer.NODES + 0.6}>
      {itemControls.items.map((item) => (
        <SelectionHighlight
          key={`selection-${item.type}-${item.id}`}
          item={item}
          scene={scene}
        />
      ))}
    </SceneLayer>
  );
};

// Individual selection highlight for each item
interface SelectionHighlightProps {
  item: ItemReference;
  scene: any;
}

const SelectionHighlight: React.FC<SelectionHighlightProps> = ({
  item,
  scene
}) => {
  // Calculate bounding box for the item
  const bbox = React.useMemo(() => {
    let fromX = 0;
    let fromY = 0;
    let toX = 0;
    let toY = 0;

    if (item.type === 'ITEM') {
      const sceneItem = scene.items?.find((i: any) => i.id === item.id);
      if (sceneItem) {
        // Simple: highlight the tile, not the icon
        fromX = sceneItem.tile.x;
        fromY = sceneItem.tile.y;
        toX = sceneItem.tile.x;
        toY = sceneItem.tile.y;
      }
    } else if (item.type === 'RECTANGLE') {
      const rectangle = scene.rectangles?.find((r: any) => r.id === item.id);
      if (rectangle) {
        fromX = Math.min(rectangle.from.x, rectangle.to.x);
        fromY = Math.min(rectangle.from.y, rectangle.to.y);
        toX = Math.max(rectangle.from.x, rectangle.to.x);
        toY = Math.max(rectangle.from.y, rectangle.to.y);
      }
    } else if (item.type === 'TEXTBOX') {
      const textbox = scene.textBoxes?.find((t: any) => t.id === item.id);
      if (textbox) {
        fromX = textbox.tile.x;
        fromY = textbox.tile.y;
        const width = textbox.size?.width || 2;
        toX = textbox.tile.x + width;
        toY = textbox.tile.y;
      }
    } else if (item.type === 'CONNECTOR') {
      const connector = scene.connectors?.find((c: any) => c.id === item.id);
      if (connector?.path?.rectangle) {
        fromX = Math.min(
          connector.path.rectangle.from.x,
          connector.path.rectangle.to.x
        )+1;
        fromY = Math.min(
          connector.path.rectangle.from.y,
          connector.path.rectangle.to.y
        )+1;
        toX = Math.max(
          connector.path.rectangle.from.x,
          connector.path.rectangle.to.x
        ) -1;
        toY = Math.max(
          connector.path.rectangle.from.y,
          connector.path.rectangle.to.y
        ) -1;
      }
    }

    const result = {
      from: { x: fromX, y: fromY },
      to: { x: toX, y: toY }
    };

    return result;
  }, [item, scene]);

  const { css, pxSize } = useIsoProjection({ from: bbox.from, to: bbox.to });

  if (!pxSize.width || !pxSize.height) {
    return null;
  }

  const strokeWidth = 3;
  const padding = 0; // Zero padding - show exact tile boundary
  const selectionColor = '#3b82f6'; // Blue color for selection

  // Calculate viewbox size that includes padding
  const viewboxSize = {
    width: pxSize.width + padding * 2,
    height: pxSize.height + padding * 2
  };

  return (
    <Svg
      viewboxSize={viewboxSize}
      style={{
        ...css,
        // Adjust position to account for padding
        left: (css.left as number) - padding,
        top: (css.top as number) - padding,
        pointerEvents: 'none',
        filter: `drop-shadow(0 0 8px ${selectionColor}60)`
      }}
    >
      <g>
        <rect
          x="0"
          y="0"
          width={pxSize.width + padding * 2}
          height={pxSize.height + padding * 2}
          fill="none"
          stroke={selectionColor}
          strokeWidth={strokeWidth}
          strokeDasharray={`${strokeWidth * 3} ${strokeWidth * 2}`}
          strokeLinecap="round"
          rx={6}
          opacity={0.9}
        >
          {/* Pulse animation */}
          <animate
            attributeName="stroke-dashoffset"
            from="0"
            to={strokeWidth * 5}
            dur="1s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.6;1;0.6"
            dur="2s"
            repeatCount="indefinite"
          />
        </rect>

        {/* Semi-transparent fill for visibility */}
        <rect
          x="0"
          y="0"
          width={pxSize.width + padding * 2}
          height={pxSize.height + padding * 2}
          fill={selectionColor}
          opacity={0.05}
          rx={6}
        />

        {/* Corner markers */}
        {[
          { x: 0, y: 0 },
          { x: pxSize.width + padding * 2, y: 0 },
          { x: 0, y: pxSize.height + padding * 2 },
          { x: pxSize.width + padding * 2, y: pxSize.height + padding * 2 }
        ].map((corner, i) => (
          <circle
            key={i}
            cx={corner.x}
            cy={corner.y}
            r="4"
            fill={selectionColor}
            opacity="0.8"
          >
            <animate
              attributeName="r"
              values="3;5;3"
              dur="2s"
              repeatCount="indefinite"
            />
          </circle>
        ))}
      </g>
    </Svg>
  );
};
